import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  Tooltip,
  TableRow,
  TableCell
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Speed as DifficultyIcon,
  ShoppingCart as ProductIcon
} from '@mui/icons-material';
import MUIDataTable from "mui-datatables";
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
// Предполагается, что apiService настроен для вызовов к вашему backend
import apiService from '../services/api';
import { getImageUrl } from '../config/api.config';
import { useAuth } from '../contexts/AuthContext';
import ScanResultDetails from '../components/ScanResultDetails';

const AdminScanHistoryPage = () => {
  const { canEdit } = useAuth();
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [storageMode, setStorageMode] = useState('local'); // Режим хранения файлов

  // Серверный режим таблицы: пагинация, сортировка, поиск и фильтры обрабатываются на бэкенде
  const [page, setPage] = useState(0); // mui-datatables нумерует страницы с 0
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState({ name: 'timestamp', direction: 'desc' });
  const [searchText, setSearchText] = useState('');
  const [filterList, setFilterList] = useState([[], [], [], [], [], []]); // по массиву на каждую колонку
  const [plantNameOptions, setPlantNameOptions] = useState([]);

  // Состояние выгрузки CSV (выгружаются все строки, подходящие под текущие фильтры)
  const [csvLoading, setCsvLoading] = useState(false);

  // Загрузка текущей страницы истории с сервера (пагинация/сортировка/поиск/фильтры - на бэкенде)
  const fetchScanHistory = async (params = {}) => {
    const currentPage = params.page ?? page;
    const currentRowsPerPage = params.rowsPerPage ?? rowsPerPage;
    const currentSortOrder = params.sortOrder ?? sortOrder;
    const currentSearchText = params.searchText ?? searchText;
    const currentFilterList = params.filterList ?? filterList;

    setLoading(true);
    console.log('[AdminScanHistory] fetchScanHistory: start', { page: currentPage, rowsPerPage: currentRowsPerPage, sortOrder: currentSortOrder, search: currentSearchText });
    try {
      const response = await apiService.adminScanHistory.getScanHistory(
        currentPage + 1, // API нумерует страницы с 1
        currentRowsPerPage,
        {
          search: currentSearchText,
          sortBy: currentSortOrder.name,
          sortOrder: currentSortOrder.direction,
          plantNames: currentFilterList[1] || [], // колонка "Название растения"
        }
      );

      if (!response.data.success) {
        console.error('[AdminScanHistory] fetchScanHistory: API error', response.data);
        setSnackbar({ open: true, message: response.data.message || 'Не удалось загрузить историю сканирований', severity: 'error' });
        return;
      }

      const { history: pageHistory, total: totalCount } = response.data.data;
      console.log('[AdminScanHistory] fetchScanHistory: loaded', pageHistory.length, 'из', totalCount);

      // Определяем режим хранения на основе первого URL
      const firstPhoto = pageHistory.find(item => item.photo);
      if (firstPhoto && firstPhoto.photo && firstPhoto.photo.startsWith('http')) {
        setStorageMode('cloudinary');
      }

      setHistory(pageHistory);
      setTotal(totalCount);
    } catch (error) {
      console.error('[AdminScanHistory] fetchScanHistory: catch error', error);
      setSnackbar({ open: true, message: 'Произошла ошибка при загрузке данных', severity: 'error' });
    } finally {
      setLoading(false);
      console.log('[AdminScanHistory] fetchScanHistory: end');
    }
  };

  useEffect(() => {
    fetchScanHistory();

    // Список уникальных названий растений для multiselect-фильтра
    apiService.adminScanHistory.getPlantNames()
      .then((response) => {
        if (response.data.success) setPlantNameOptions(response.data.data);
      })
      .catch((error) => console.error('[AdminScanHistory] Не удалось загрузить список названий растений', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Дебаунс поиска, чтобы не слать запрос на каждое нажатие клавиши
  const searchTimerRef = useRef(null);
  const debouncedFetch = (params) => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchScanHistory(params), 500);
  };

  // Серверный режим: любое действие таблицы приводит к перезапросу данных
  const handleTableChange = (action, tableState) => {
    // Отменяем отложенный поиск только при смене страницы/сортировки/фильтров.
    // Важно: mui-datatables шлёт служебное действие 'propsUpdate' при каждом
    // перерендере (options/columns/data пересоздаются), поэтому нельзя
    // сбрасывать таймер на любое действие, отличное от 'search', — иначе
    // дебаунс поиска отменяется раньше, чем успевает сработать.
    if (['changePage', 'changeRowsPerPage', 'sort', 'filterChange', 'resetFilters'].includes(action)) {
      clearTimeout(searchTimerRef.current);
    }
    switch (action) {
      case 'changePage':
        setPage(tableState.page);
        fetchScanHistory({ page: tableState.page });
        break;
      case 'changeRowsPerPage':
        setPage(tableState.page);
        setRowsPerPage(tableState.rowsPerPage);
        fetchScanHistory({ page: tableState.page, rowsPerPage: tableState.rowsPerPage });
        break;
      case 'sort':
        setSortOrder(tableState.sortOrder);
        fetchScanHistory({ sortOrder: tableState.sortOrder });
        break;
      case 'search':
        setPage(0);
        setSearchText(tableState.searchText || '');
        debouncedFetch({ page: 0, searchText: tableState.searchText || '' });
        break;
      case 'filterChange':
        setPage(0);
        setFilterList(tableState.filterList);
        fetchScanHistory({ page: 0, filterList: tableState.filterList });
        break;
      case 'resetFilters':
        setPage(0);
        setFilterList([[], [], [], [], [], []]);
        fetchScanHistory({ page: 0, filterList: [[], [], [], [], [], []] });
        break;
      default:
        break;
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Массовое удаление выбранных сканирований (чекбоксы в таблице)
  const handleRowsDelete = async (rowsDeleted) => {
    const ids = rowsDeleted.data
      .map(({ dataIndex }) => history[dataIndex]?._id)
      .filter(Boolean);

    if (ids.length === 0) return false;
    if (!window.confirm(`Удалить выбранные записи (${ids.length} шт.)? Это действие необратимо.`)) {
      return false;
    }

    try {
      const response = await apiService.adminScanHistory.deleteBatch(ids);
      if (response.data.success) {
        setSnackbar({ open: true, message: response.data.message || `Удалено записей: ${ids.length}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: response.data.message || 'Не удалось удалить записи', severity: 'error' });
      }
    } catch (error) {
      console.error('[AdminScanHistory] Ошибка удаления:', error);
      setSnackbar({ open: true, message: 'Произошла ошибка при удалении записей', severity: 'error' });
    }

    // Перезапрашиваем текущую страницу; возврат false отменяет локальное удаление строк mui-datatables
    fetchScanHistory();
    return false;
  };

  // --- Выгрузка CSV всех строк (с учётом текущих поиска/фильтров/сортировки) ---

  // Экранирование значения для CSV (разделитель ';' + BOM — корректно открывается в Excel с кириллицей)
  const csvEscape = (value) => {
    if (value === null || value === undefined) return '';
    return `"${String(value).replace(/"/g, '""')}"`;
  };

  // Плоское текстовое представление пользователя (без React-разметки)
  const getUserCsvText = (user) => {
    if (!user) return 'Неизвестный пользователь';
    if (typeof user === 'object') {
      const { email, phone, authMethod, displayContact, name } = user;
      const contact = displayContact || (authMethod === 'email' ? email : phone) || email || phone || '';
      return [contact, name].filter(Boolean).join(' ');
    }
    return `ID: ${user}`;
  };

  const getResultCsvText = (result) => {
    const info = result?.plant_info;
    if (!info) return 'Нет данных';
    const parts = [info.is_healthy ? 'Здоровое' : 'Есть проблемы'];
    if (info.difficulty_level) {
      parts.push(info.difficulty_level === 'easy' ? 'Легкий уход' :
        info.difficulty_level === 'medium' ? 'Средний уход' : 'Сложный уход');
    }
    return parts.join(', ');
  };

  // Извлекатели плоских значений по имени колонки таблицы
  const csvExtractors = {
    timestamp: (item) => {
      const date = item.timestamp || item.createdAt;
      return date ? new Date(date).toLocaleString('ru-RU') : '';
    },
    user: (item) => getUserCsvText(item.user),
    photo: (item) => (item.photo ? getImageUrl(item.photo, storageMode) : 'Нет фото'),
    plant_name: (item) => {
      const info = item.result?.plant_info;
      if (!info) return '';
      return [info.name, info.latin_name].filter(Boolean).join(' / ');
    },
    result: (item) => getResultCsvText(item.result),
    products: (item) => (Array.isArray(item.result?.recommended_products) ? item.result.recommended_products : [])
      .map((p) => p.product_name).filter(Boolean).join('; '),
  };

  // Догружаем с сервера ВСЕ страницы, подходящие под текущие поиск/фильтры/сортировку
  const fetchAllForCsv = async () => {
    const EXPORT_PAGE_SIZE = 200; // совпадает с верхним лимитом на бэкенде
    const all = [];
    let pageNum = 1;
    let totalCount = Infinity;
    while (all.length < totalCount) {
      const response = await apiService.adminScanHistory.getScanHistory(pageNum, EXPORT_PAGE_SIZE, {
        search: searchText,
        sortBy: sortOrder.name,
        sortOrder: sortOrder.direction,
        plantNames: filterList[1] || [],
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Не удалось загрузить данные для выгрузки');
      }
      const { history: chunk, total: totalCount2 } = response.data.data;
      totalCount = totalCount2;
      all.push(...chunk);
      if (chunk.length < EXPORT_PAGE_SIZE) break;
      pageNum += 1;
    }
    return all;
  };

  // Кастомная выгрузка: все строки по текущим фильтрам, а не только текущая страница
  const handleCsvDownload = async (columnsMeta) => {
    if (csvLoading) return; // защита от повторного клика во время выгрузки
    setCsvLoading(true);
    try {
      const items = await fetchAllForCsv();
      // Учитываем только видимые колонки (панель "Просмотр столбцов")
      const visibleColumns = columnsMeta.filter((c) => c.display !== 'false' && csvExtractors[c.name]);
      const header = visibleColumns.map((c) => csvEscape(c.label)).join(';');
      const rows = items.map((item) =>
        visibleColumns.map((c) => csvEscape(csvExtractors[c.name](item))).join(';')
      );
      const csv = String.fromCharCode(0xFEFF) + [header, ...rows].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `История сканирований.${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: `Выгружено записей: ${items.length}`, severity: 'success' });
    } catch (error) {
      console.error('[AdminScanHistory] Ошибка выгрузки CSV:', error);
      setSnackbar({ open: true, message: error.message || 'Произошла ошибка при выгрузке CSV', severity: 'error' });
    } finally {
      setCsvLoading(false);
    }
  };

  // Форматирование данных о пользователе
  const formatUser = (user) => {
    if (!user) return 'Неизвестный пользователь';

    const userId = typeof user === 'string' ? user : user._id;

    // Полный идентификатор пользователя, копируется в буфер по клику
    const idChip = (
      <Chip
        label={`ID: ${userId || 'Неизвестно'}`}
        size="small"
        variant="outlined"
        color="info"
        clickable={!!userId}
        onClick={() => {
          if (!userId) return;
          navigator.clipboard.writeText(userId);
          setSnackbar({ open: true, message: `Скопировано: ${userId}`, severity: 'success' });
        }}
        sx={{ mt: 0.5, alignSelf: 'flex-start', fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: '100%' }}
      />
    );

    // Если есть дополнительная информация о пользователе
    if (typeof user === 'object') {
      const { email, phone, authMethod, displayContact, name } = user;

      // Приоритет отображения: displayContact > email/phone по authMethod > любой доступный контакт
      let primaryContact = displayContact;
      if (!primaryContact) {
        primaryContact = authMethod === 'email' ? email : phone;
      }
      if (!primaryContact) {
        primaryContact = email || phone;
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {primaryContact && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {(authMethod === 'email' || email === primaryContact) ? (
                <EmailIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
              ) : (
                <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {primaryContact}
              </Typography>
            </Box>
          )}
          {name && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
              {name}
            </Typography>
          )}
          {idChip}
        </Box>
      );
    }

    // Если только ID
    return idChip;
  };

  // Форматирование фото с универсальной поддержкой
  const formatPhoto = (photoPath) => {
    if (!photoPath) return 'Нет фото';

    // Получаем URL изображения используя универсальную функцию
    const imagePath = getImageUrl(photoPath, storageMode);

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
        {/* Фото растения */}
        <Box
          component="img"
          src={imagePath}
          alt="Фото растения"
          sx={{
            width: 80,
            height: 80,
            objectFit: 'cover',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'transform 0.3s',
            '&:hover': {
              transform: 'scale(1.1)'
            }
          }}
          onClick={() => window.open(imagePath, '_blank')}
          onError={(e) => {
            console.error('[AdminScanHistory] Не удалось загрузить изображение:', imagePath);

            // Скрываем сломанное изображение и показываем заглушку
            e.target.style.display = 'none';

            // Создаем заглушку только если её ещё нет
            if (!e.target.nextElementSibling || !e.target.nextElementSibling.classList.contains('image-fallback')) {
              const fallbackIcon = document.createElement('div');
              fallbackIcon.className = 'image-fallback';
              fallbackIcon.style = `
                width: 80px;
                height: 80px;
                border-radius: 8px;
                background-color: rgba(0, 0, 0, 0.08);
                display: flex;
                justify-content: center;
                align-items: center;
                border: 1px dashed rgba(0, 0, 0, 0.2);
              `;
              fallbackIcon.innerHTML = `
                <svg style="width: 40px; height: 40px; color: rgba(0, 0, 0, 0.54);" 
                     focusable="false" aria-hidden="true" viewBox="0 0 24 24">
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"></path>
                </svg>
              `;
              e.target.parentNode.appendChild(fallbackIcon);
            }
          }}
        />
      </Box>
    );
  };

  // Форматирование названия растения
  const formatPlantName = (result) => {
    if (!result || !result.plant_info) return 'Нет данных';

    const plantInfo = result.plant_info;
    const name = plantInfo.name || 'Неизвестное растение';
    const latinName = plantInfo.latin_name;

    return (
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {name}
        </Typography>
        {latinName && (
          <Typography variant="caption" color="text.secondary">
            <i>{latinName}</i>
          </Typography>
        )}
      </Box>
    );
  };

  // Форматирование результата: только самое важное (статус, сложность ухода, теги).
  // Токсичность и прочие детали смотрятся в раскрывающейся строке.
  const formatResult = (result) => {
    if (!result || !result.plant_info) return 'Нет данных';

    const plantInfo = result.plant_info;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 300 }}>
        {/* Состояние растения */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={plantInfo.is_healthy ? 'Здоровое' : 'Проблемы'}
            color={plantInfo.is_healthy ? 'success' : 'error'}
            size="small"
          />
          {plantInfo.difficulty_level && (
            <Chip
              icon={<DifficultyIcon />}
              label={plantInfo.difficulty_level === 'easy' ? 'Легкий' :
                plantInfo.difficulty_level === 'medium' ? 'Средний' : 'Сложный'}
              color={plantInfo.difficulty_level === 'easy' ? 'success' :
                plantInfo.difficulty_level === 'medium' ? 'warning' : 'error'}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {/* Теги */}
        {plantInfo.tags && plantInfo.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {plantInfo.tags.slice(0, 3).map((tag, index) => (
              <Chip key={index} label={tag} size="small" variant="outlined" />
            ))}
            {plantInfo.tags.length > 3 && (
              <Chip label={`+${plantInfo.tags.length - 3}`} size="small" variant="outlined" />
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Форматирование рекомендованных товаров сканирования
  const formatProducts = (products) => {
    if (!products || products.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Нет товаров
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 280 }}>
        {products.map((product, index) => {
          const tooltipParts = [product.disease_name, product.product_type, product.dosage].filter(Boolean);
          const chip = (
            <Chip
              icon={product.product_image ? undefined : <ProductIcon />}
              avatar={product.product_image ? <Avatar src={product.product_image} /> : undefined}
              label={product.product_name || 'Товар'}
              size="small"
              color="primary"
              variant="outlined"
              clickable={!!product.purchase_link}
              component={product.purchase_link ? 'a' : 'div'}
              href={product.purchase_link || undefined}
              target={product.purchase_link ? '_blank' : undefined}
              rel={product.purchase_link ? 'noopener noreferrer' : undefined}
            />
          );

          return tooltipParts.length > 0 ? (
            <Tooltip key={product._id || index} title={tooltipParts.join(' • ')}>
              {chip}
            </Tooltip>
          ) : (
            <React.Fragment key={product._id || index}>{chip}</React.Fragment>
          );
        })}
      </Box>
    );
  };

  // Рендер подробных деталей сканирования (показывается в раскрывающейся строке таблицы)
  // Вся разметка вынесена в переиспользуемый компонент ScanResultDetails
  const renderScanDetails = (result) => (
    <ScanResultDetails
      plantInfo={result?.plant_info}
      recommendedProducts={result?.recommended_products}
    />
  );

  const columns = [
    {
      name: "photo",
      label: "Фото",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => formatPhoto(value)
      }
    },
    {
      name: "plant_name",
      label: "Название растения",
      options: {
        filter: true,
        sort: true,
        filterOptions: { names: plantNameOptions }, // серверный multiselect-фильтр
        customBodyRender: (value) => formatPlantName(value)
      }
    },
    {
      name: "user",
      label: "Пользователь",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => formatUser(value)
      }
    },
    {
      name: "result",
      label: "Результат",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => formatResult(value)
      }
    },
    {
      name: "products",
      label: "Рекомендованные товары",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => formatProducts(value)
      }
    },
    {
      name: "timestamp",
      label: "Дата сканирования",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography variant="body2">
            {value ? new Date(value).toLocaleString('ru-RU') : 'Нет даты'}
          </Typography>
        )
      }
    },
  ];

  const data = history.map((item) => ({
    photo: item.photo,
    plant_name: item.result, // рендерится через formatPlantName
    user: item.user,
    result: item.result,
    products: Array.isArray(item.result?.recommended_products) ? item.result.recommended_products : [],
    timestamp: item.timestamp || item.createdAt,
  }));

  const options = {
    storageKey: 'scan-history-table',
    jumpToPage: true,
    serverSide: true, // пагинация, сортировка, поиск и фильтры обрабатываются на сервере
    count: total,
    page,
    rowsPerPage,
    sortOrder,
    searchText,
    filterList,
    onTableChange: handleTableChange,
    // Чекбоксы выделения строк с массовым удалением (только для ролей с правом редактирования)
    selectableRows: canEdit ? 'multiple' : 'none',
    onRowsDelete: handleRowsDelete,
    rowsPerPageOptions: [10, 25, 50, 100],
    filterType: 'multiselect',
    expandableRows: true,
    isRowExpandable: (dataIndex) => !!history[dataIndex]?.result?.plant_info,
    renderExpandableRow: (rowData, rowMeta) => {
      const originalData = history[rowMeta.dataIndex];
      const colSpan = rowData.length + 1;
      return (
        <TableRow>
          <TableCell colSpan={colSpan}>
            {renderScanDetails(originalData?.result)}
          </TableCell>
        </TableRow>
      );
    },
    // Кастомная выгрузка CSV: все строки по текущим фильтрам, а не только текущая страница.
    // Возврат false отменяет стандартную выгрузку mui-datatables.
    onDownload: (buildHead, buildBody, columnsMeta) => {
      handleCsvDownload(columnsMeta);
      return false;
    },
    textLabels: {
      body: {
        noMatch: loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>) : ('История сканирований пуста'),
        toolTip: "Сортировка",
        columnHeaderTooltip: (column) => `Сортировка для ${column.label}`
      },
      pagination: {
        next: "Следующая страница",
        previous: "Предыдущая страница",
        rowsPerPage: "Строк на странице:",
        displayRows: "из",
        jumpToPage: "Перейти на страницу:",
      },
      toolbar: {
        search: "Поиск",
        downloadCsv: "Скачать CSV",
        print: "Печать",
        viewColumns: "Просмотр столбцов",
        filterTable: "Фильтр таблицы",
      },
      filter: {
        all: "Все",
        title: "ФИЛЬТРЫ",
        reset: "СБРОС",
      },
      viewColumns: {
        title: "Показать столбцы",
        titleAria: "Показать/скрыть столбцы таблицы",
      },
      selectedRows: {
        text: "выбрано",
        delete: "Удалить",
        deleteAria: "Удалить выбранные строки",
      },
    },
  };

  const appTheme = useTheme();
  const getMuiTheme = () => createTheme(appTheme, {
    components: {
      MUIDataTableToolbar: {
        styleOverrides: {
          filterPaper: {
            width: '25%',
            minWidth: '20vw',
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            flexBasis: '100% !important',
            maxWidth: '100% !important',
            width: '100%',
            marginLeft: '0',
          },
          item: {
            paddingLeft: '0 !important',
            paddingTop: '1rem !important',
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '1rem',
            minWidth: '20vw !important',
            padding: '0.5rem !important',
          }
        }
      },
      MUIDataTableViewCol: {
        styleOverrides: {
          root: {
            padding: '1.5rem !important',
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '0.5rem !important',
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            margin: '0.1rem !important',
            userSelect: 'text',
          }
        }
      },
    },
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            История сканирований пользователей
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Просмотр всех сканирований растений, выполненных пользователями через мобильное приложение
          </Typography>
          {total > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Всего записей: {total}
            </Typography>
          )}
        </Box>
      </Box>

      <ThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          title={"История сканирований"}
          data={data}
          columns={columns}
          options={options}
        />
      </ThemeProvider>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminScanHistoryPage; 
